/**
 * Toast Notification Container
 * Displays toast notifications from the UI store
 */

import React from 'react';
import { useUIStore, Toast } from '@flow-studio/store'; // import { uiStore';
import { CheckIcon, CloseIcon, WarningIcon, InfoIcon } from '@bizfirst/common-react';

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast, theme } = useUIStore();

  if (toasts.length === 0) {
    return null;
  }

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '12px',
    pointerEvents: 'none',
  };

  return (
    <div style={containerStyle}>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
          theme={theme}
        />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
  theme: 'light' | 'dark';
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose, theme }) => {
  const getToastStyles = () => {
    const baseStyle: React.CSSProperties = {
      padding: '16px 20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '300px',
      maxWidth: '500px',
      pointerEvents: 'auto',
      animation: 'slideIn 0.3s ease-out',
      fontSize: '14px',
      fontWeight: 500,
    };

    const typeColors = {
      success: {
        background: theme === 'dark' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
        border: theme === 'dark' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(34, 197, 94, 0.2)',
        color: theme === 'dark' ? '#4ade80' : '#16a34a',
      },
      error: {
        background: theme === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
        border: theme === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
        color: theme === 'dark' ? '#f87171' : '#dc2626',
      },
      warning: {
        background: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)',
        border: theme === 'dark' ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(251, 191, 36, 0.2)',
        color: theme === 'dark' ? '#fbbf24' : '#d97706',
      },
      info: {
        background: theme === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
        border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
        color: theme === 'dark' ? '#60a5fa' : '#2563eb',
      },
    };

    const typeStyle = typeColors[toast.type];

    return {
      ...baseStyle,
      background: typeStyle.background,
      border: typeStyle.border,
      color: typeStyle.color,
    };
  };

  const iconStyle: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 'bold',
    flexShrink: 0,
  };

  const messageStyle: React.CSSProperties = {
    flex: 1,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    padding: '4px',
    fontSize: '18px',
    lineHeight: 1,
    opacity: 0.7,
    transition: 'opacity 0.2s',
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <CheckIcon size={18} />;
      case 'error':   return <CloseIcon size={18} />;
      case 'warning': return <WarningIcon size={18} />;
      case 'info':    return <InfoIcon size={18} />;
    }
  };

  return (
    <div style={getToastStyles()}>
      <span style={iconStyle}>{getIcon()}</span>
      <span style={messageStyle}>{toast.message}</span>
      <button
        style={closeButtonStyle}
        onClick={onClose}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
      >
        ×
      </button>
    </div>
  );
};

// Add keyframes for slide-in animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
document.head.appendChild(style);
